import {
  Avatar, Box, Chip, Stack, Tooltip, Typography,
} from '@mui/joy';
import { animated, useSpringRef, useTransition } from '@react-spring/web';
import details from 'assets/Details';
import ProgressiveImage from 'components/ProgressiveImage';
import moment from 'moment';
import React, { useEffect } from 'react';
import { BsCode } from 'react-icons/bs';
import { FiChevronRight, FiPackage } from 'react-icons/fi';
import { GoOrganization } from 'react-icons/go';
import { IoSchoolOutline } from 'react-icons/io5';
import { FaHandsHelping } from 'react-icons/fa';
import { SlWrench } from 'react-icons/sl';
import { TbCircleDashed } from 'react-icons/tb';
import { Category } from './sections/Terminal';

export const skillIcons: {
  [key: string]: React.ReactNode;
} = {
  languages: <BsCode />,
  frameworks: <FiPackage />,
  tools: <SlWrench />,
  others: <TbCircleDashed />,
};

// Format date as timestamp style for Education: "Jul 2023" → "2023.07"
const formatEduTimestamp = (dateStr: string): string => {
  if (dateStr === 'Present') return 'Present';
  const date = moment(dateStr, 'MMM YYYY');
  return date.format('YYYY.MM');
};

export function Education({ wrap } : {wrap?: boolean} = { wrap: false }) {
  return (
    <Stack direction={wrap ? 'row' : 'column'} flexWrap="wrap" justifyContent="space-between" gap={2} p={1} sx={{ overflow: 'hidden' }}>
      {details.education.map((item) => (
        <Stack direction="row" gap={1.5} key={`${item.school}-${item.major}-${item.start}-${item.end}`}>
          <Avatar
            color="neutral"
            variant="soft"
            size="lg"
            sx={(theme) => ({
              borderRadius: theme.getCssVar('radius-md'),
              border: `1px solid ${theme.getCssVar('palette-divider')}`,
            })}
          >
            {item.icon ? (
              <ProgressiveImage
                src={item.icon}
                placeholder={item.iconMin}
                alt={item.school}
                style={{
                  width: '100%',
                  height: '100%',
                }}
              />
            ) : (<IoSchoolOutline />)}
          </Avatar>
          <Stack>
            <Typography level="body1" display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography
                component="span"
                sx={{
                  fontFamily: "'Fira Code', monospace",
                  color: 'primary.400',
                  fontSize: '0.85em',
                }}
              >
                {'>_'}
              </Typography>
              {item.url ? (
                <Typography
                  component="a"
                  href={item.url}
                  textColor="inherit"
                  target="_blank"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {item.school}
                </Typography>
              ) : item.school }
              <Typography
                level="body2"
                component="span"
                sx={{
                  fontFamily: "'Fira Code', monospace",
                  color: 'text.tertiary',
                  fontSize: '0.8em',
                }}
              >
                [
                {formatEduTimestamp(item.start)}
                {' → '}
                {formatEduTimestamp(item.end)}
                ]
              </Typography>
            </Typography>
            <Typography
              level="body2"
              sx={{
                fontFamily: "'Fira Code', monospace",
                fontSize: '0.85em',
              }}
            >
              {item.degree}
              {' - '}
              {item.major}
            </Typography>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

function AcademicSection({ title, children }: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="details"
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&[open] > summary': { color: 'text.primary' },
        '&[open] .academic-section-chevron': { transform: 'rotate(90deg)' },
        '@media (prefers-reduced-motion: reduce)': {
          '& > summary, & .academic-section-chevron': { transition: 'none' },
        },
      }}
    >
      <Box
        component="summary"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 44,
          boxSizing: 'border-box',
          py: 0.75,
          color: 'text.secondary',
          cursor: 'pointer',
          listStyle: 'none',
          transition: 'color 0.2s ease',
          '&::-webkit-details-marker': { display: 'none' },
          '&:hover': { color: 'text.primary' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.400',
            outlineOffset: -2,
          },
        }}
      >
        <Box
          component="span"
          className="academic-section-chevron"
          aria-hidden="true"
          sx={{
            display: 'flex',
            flexShrink: 0,
            transition: 'transform 0.2s ease',
          }}
        >
          <FiChevronRight size={16} />
        </Box>
        <Typography
          component="span"
          level="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            color: 'inherit',
            fontWeight: 'md',
            overflowWrap: 'anywhere',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{
        pt: 0.5,
        pb: 1.5,
        pl: 3,
        '& .MuiChip-root': { maxWidth: '100%' },
        '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere' },
      }}
      >
        {children}
      </Box>
    </Box>
  );
}

export function Coursework() {
  // Helper to get chip styles - supports MUI color names or custom hex
  const getChipSx = (color: string) => {
    const isCustomColor = color.startsWith('#');
    if (isCustomColor) {
      return {
        borderColor: color,
        color,
        fontFamily: "'Fira Code', monospace",
        fontSize: '0.7rem',
        transition: 'all 0.2s ease-in-out',
        cursor: 'default',
        '&:hover': {
          backgroundColor: `${color}20`,
          borderColor: color,
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${color}30`,
        },
      };
    }
    return {
      fontFamily: "'Fira Code', monospace",
      fontSize: '0.7rem',
      transition: 'all 0.2s ease-in-out',
      cursor: 'default',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      },
    };
  };

  const getChipColor = (color: string): 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info' => {
    if (color.startsWith('#')) return 'neutral';
    return color as 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  };

  return (
    <Stack p={1} sx={{ overflow: 'hidden' }}>
      {/* Coursework Section */}
      <AcademicSection title="sel_undergrad_coursework">
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {details.coursework.map((course) => (
            <Tooltip
              key={course.name}
              title={
                course.instructor ? (
                  <Typography
                    sx={{
                      fontFamily: "'Fira Code', monospace",
                      fontSize: '0.7rem',
                      '&::after': {
                        content: '"▌"',
                        animation: 'blink 1s infinite',
                        color: 'primary.400',
                        ml: 0.3,
                      },
                      '@keyframes blink': {
                        '0%, 50%': { opacity: 1 },
                        '51%, 100%': { opacity: 0 },
                      },
                    }}
                  >
                    {course.instructor}
                  </Typography>
                ) : null
              }
              placement="top"
              arrow
              enterDelay={150}
              variant="outlined"
              sx={(theme) => ({
                backgroundColor: theme.palette.background.surface,
                borderColor: theme.palette.divider,
              })}
            >
              <Chip
                variant="outlined"
                color={getChipColor(course.color)}
                size="sm"
                sx={getChipSx(course.color)}
              >
                {course.name}
              </Chip>
            </Tooltip>
          ))}
        </Stack>
      </AcademicSection>

      {/* researchAssistantships Section */}
      {details.researchAssistantships && details.researchAssistantships.length > 0 && (
        <AcademicSection title="undergradResearch (RA)">
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {details.researchAssistantships.map((item) => (
              <Chip
                key={item.name}
                variant="outlined"
                color={getChipColor(item.color)}
                size="sm"
                sx={getChipSx(item.color)}
              >
                {item.name}
              </Chip>
            ))}
          </Stack>
        </AcademicSection>
      )}
    </Stack>
  );
}

// Format date as timestamp style: "Jun 2024" → "2024.06"
const formatTimestamp = (dateStr: string): string => {
  if (dateStr === 'Present') return 'Present';
  const date = moment(dateStr, 'MMM YYYY');
  return date.format('YYYY.MM');
};

export function Experience() {
  // Group consecutive experiences by company
  const groupedExperiences = details.experience.reduce((acc, item) => {
    const lastItem = acc[acc.length - 1];
    if (lastItem && lastItem.length && lastItem[0].company === item.company) {
      lastItem.push(item);
      return acc;
    }
    return [...acc, [item]];
  }, [] as typeof details.experience[number][][]);

  // Calculate total duration for a group of positions
  const calculateTotalDuration = (items: typeof details.experience) => {
    const sortedItems = [...items].sort((a, b) => moment(a.start, 'MMM YYYY').valueOf() - moment(b.start, 'MMM YYYY').valueOf());
    const earliestStart = moment(sortedItems[0].start, 'MMM YYYY').toDate();
    const latestEndItem = sortedItems.find((item) => item.end === 'Present')
      || sortedItems.reduce((latest, item) => (moment(item.end, 'MMM YYYY').isAfter(moment(latest.end, 'MMM YYYY')) ? item : latest));
    const latestEnd = latestEndItem.end === 'Present'
      ? new Date(Date.now())
      : moment(latestEndItem.end, 'MMM YYYY').toDate();

    const momentDuration = moment.duration(latestEnd.getTime() - earliestStart.getTime());
    const years = momentDuration.years();
    const months = momentDuration.months() + 1;
    return `${years > 0 ? `${years} yr${years !== 1 ? 's' : ''} ` : ''}${months} mo${months !== 1 ? 's' : ''}`;
  };

  return (
    <Stack gap={2} p={1} sx={{ overflow: 'hidden' }}>
      {groupedExperiences.map((items, groupIndex) => {
        const isLastGroup = groupIndex === groupedExperiences.length - 1;
        const hasMultiplePositions = items.length > 1;

        // Calculate duration for single position
        const singleDuration = !hasMultiplePositions ? (() => {
          const startDate = moment(items[0].start, 'MMM YYYY').toDate();
          const endDate = moment(items[0].end === 'Present' ? new Date(Date.now()) : items[0].end, 'MMM YYYY').toDate();
          const momentDuration = moment.duration(endDate.getTime() - startDate.getTime());
          const years = momentDuration.years();
          const months = momentDuration.months() + 1;
          return `${years > 0 ? `${years} yr${years !== 1 ? 's' : ''} ` : ''}${months} mo${months !== 1 ? 's' : ''}`;
        })() : '';

        return (
          <Stack
            direction="row"
            gap={1.5}
            key={`${items[0].company}-${items[0].start}`}
          >
            <Avatar
              color="neutral"
              variant="soft"
              size="lg"
              sx={(theme) => ({
                borderRadius: theme.getCssVar('radius-md'),
                border: `1px solid ${theme.getCssVar('palette-divider')}`,
              })}
            >
              {items[0].icon ? (
                <ProgressiveImage
                  src={items[0].icon}
                  placeholder={items[0].iconMin}
                  alt={items[0].company}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              ) : (<GoOrganization />)}
            </Avatar>
            <Stack
              gap={hasMultiplePositions ? 0 : 0.5}
              sx={(theme) => ({
                flex: 1,
                minWidth: 0,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: '1px',
                  height: 'calc(100% - 3rem - 12px)',
                  background: isLastGroup
                    ? `linear-gradient(to bottom, ${theme.palette.divider}, transparent)`
                    : theme.palette.divider,
                  left: 'calc(-1.5rem - 12px)',
                  top: 'calc(3rem + 12px)',
                },
              })}
            >
              {/* Company Header */}
              <Typography level="body1" display="flex" alignItems="center" flexWrap="wrap" columnGap={1} rowGap={0.3}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "'Fira Code', monospace",
                    color: 'primary.400',
                    fontSize: '0.85em',
                    mr: 0.5,
                  }}
                >
                  {'>_'}
                </Typography>
                {items[0].url ? (
                  <Typography
                    component="a"
                    href={items[0].url}
                    textColor="inherit"
                    target="_blank"
                    sx={{
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {items[0].company}
                  </Typography>
                ) : items[0].company}

                {/* Only show dates for single positions */}
                {!hasMultiplePositions && (
                  <Typography
                    level="body2"
                    component="span"
                    sx={{
                      fontFamily: "'Fira Code', monospace",
                      color: 'text.tertiary',
                      fontSize: '0.8em',
                    }}
                  >
                    [
                    {formatTimestamp(items[0].start)}
                    {' → '}
                    {formatTimestamp(items[0].end)}
                    ]
                  </Typography>
                )}

                <Chip
                  size="sm"
                  variant="soft"
                  color="primary"
                  sx={{
                    ml: 0.5,
                    fontFamily: "'Fira Code', monospace",
                    fontSize: '0.7rem',
                  }}
                >
                  {hasMultiplePositions ? calculateTotalDuration(items) : singleDuration}
                </Chip>
              </Typography>

              {/* Single Position (no timeline) */}
              {!hasMultiplePositions && (
                <Stack gap={0.5}>
                  <Typography level="body2" display="flex" alignItems="baseline" flexWrap="wrap" columnGap={1} rowGap={0.3}>
                    <Typography fontWeight="lg" textColor="text.primary">
                      {items[0].position}
                    </Typography>
                    <Typography level="body2" component="span" textColor="text.secondary">
                      {items[0].contract}
                      {' '}
                      -
                      {' '}
                      {items[0].location}
                    </Typography>
                  </Typography>
                  <Stack direction="row" alignItems="start" flexWrap="wrap" gap={1} paddingY={0.5}>
                    {items[0].skills.map((skill) => (
                      <Chip
                        variant="outlined"
                        color="neutral"
                        size="sm"
                        key={skill}
                        sx={(theme) => ({
                          borderColor: theme.palette.divider,
                          fontFamily: "'Fira Code', monospace",
                          fontSize: '0.7rem',
                        })}
                      >
                        {skill}
                      </Chip>
                    ))}
                  </Stack>
                  {items[0].description && (
                    <Typography level="body3" textColor="text.tertiary" component="div">
                      {typeof items[0].description === 'string' ? items[0].description : null}
                      {typeof items[0].description === 'object' ? (
                        <Stack>
                          {items[0].description.map((chunk) => (
                            <Typography key={chunk}>
                              -
                              {' '}
                              {chunk}
                            </Typography>
                          ))}
                        </Stack>
                      ) : null}
                    </Typography>
                  )}
                </Stack>
              )}

              {/* Multiple Positions - Timeline View */}
              {hasMultiplePositions && (
                <Stack gap={0} mt={1.5}>
                  {items.map((item, subIndex) => {
                    const startDate = moment(item.start, 'MMM YYYY').toDate();
                    const endDate = moment(item.end === 'Present' ? new Date(Date.now()) : item.end, 'MMM YYYY').toDate();
                    const momentDuration = moment.duration(endDate.getTime() - startDate.getTime());
                    const years = momentDuration.years();
                    const months = momentDuration.months() + 1;
                    const duration = `${years > 0 ? `${years} yr${years !== 1 ? 's' : ''} ` : ''}${months} mo${months !== 1 ? 's' : ''}`;
                    const isFirst = subIndex === 0;
                    const isLast = subIndex === items.length - 1;

                    return (
                      <Stack
                        direction="row"
                        gap={1.5}
                        key={`${item.company}-${item.position}-${item.start}-${item.end}`}
                      >
                        {/* Timeline dot and connector */}
                        <Stack alignItems="center" sx={{ width: 16, flexShrink: 0, pt: '5px' }}>
                          <Box
                            sx={(theme) => ({
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: isFirst ? 'primary.500' : theme.palette.divider,
                              border: '2px solid',
                              borderColor: isFirst ? 'primary.200' : theme.palette.background.surface,
                              zIndex: 1,
                              flexShrink: 0,
                              boxShadow: isFirst ? `0 0 8px ${theme.palette.primary[400]}` : 'none',
                            })}
                          />
                          {!isLast && (
                            <Box
                              sx={(theme) => ({
                                width: 1.5,
                                flex: 1,
                                mt: 0.5,
                                background: theme.palette.divider,
                                opacity: 0.5,
                              })}
                            />
                          )}
                        </Stack>

                        {/* Position content */}
                        <Stack gap={0.5} pb={isLast ? 0 : 2} flex={1}>
                          <Typography level="body2" display="flex" alignItems="center" flexWrap="wrap" columnGap={1} rowGap={0.3}>
                            <Typography fontWeight="lg" textColor="text.primary">
                              {item.position}
                            </Typography>
                            <Typography
                              level="body3"
                              component="span"
                              sx={{
                                fontFamily: "'Fira Code', monospace",
                                color: 'text.tertiary',
                                fontSize: '0.7rem',
                              }}
                            >
                              [
                              {formatTimestamp(item.start)}
                              {' → '}
                              {formatTimestamp(item.end)}
                              ]
                            </Typography>
                            <Chip
                              size="sm"
                              variant="plain"
                              color="neutral"
                              sx={{
                                fontFamily: "'Fira Code', monospace",
                                fontSize: '0.6rem',
                                minHeight: 'auto',
                                py: 0,
                              }}
                            >
                              {duration}
                            </Chip>
                          </Typography>
                          <Typography
                            level="body3"
                            sx={{
                              fontFamily: "'Fira Code', monospace",
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                            }}
                          >
                            {item.contract}
                            {' · '}
                            {item.location}
                          </Typography>
                          <Stack direction="row" alignItems="start" flexWrap="wrap" gap={0.5} paddingY={0.5}>
                            {item.skills.map((skill) => (
                              <Chip
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                key={skill}
                                sx={(theme) => ({
                                  borderColor: theme.palette.divider,
                                  fontFamily: "'Fira Code', monospace",
                                  fontSize: '0.65rem',
                                })}
                              >
                                {skill}
                              </Chip>
                            ))}
                          </Stack>
                          {item.description && (
                            <Typography level="body3" textColor="text.tertiary" component="div">
                              {typeof item.description === 'string' ? item.description : null}
                              {typeof item.description === 'object' && item.description.length > 0 ? (
                                <Stack>
                                  {item.description.map((chunk) => (
                                    <Typography key={chunk}>
                                      -
                                      {' '}
                                      {chunk}
                                    </Typography>
                                  ))}
                                </Stack>
                              ) : null}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}

export function Affiliation() {
  return (
    <Stack gap={1.5} p={1} direction="row" flexWrap="wrap" justifyContent="flex-start">
      {details.affiliations.map((item) => (
        <Stack
          key={`${item.organization}-${item.start}`}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={(theme) => ({
            width: 'calc(33.333% - 12px)',
            minWidth: 0,
            [theme.breakpoints.down('md')]: {
              width: 'calc(50% - 8px)',
            },
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
        >
          <Avatar
            alt={item.organization}
            src={item.icon}
            variant="outlined"
            sx={(theme) => ({
              width: 32,
              height: 32,
              flexShrink: 0,
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.background.body,
              fontSize: '0.75rem',
            })}
          >
            {item.organization.charAt(0)}
          </Avatar>
          <Stack direction="row" alignItems="center" spacing={1} flexGrow={1} minWidth={0}>
            <Typography
              level="body2"
              textColor="inherit"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.8rem',
              }}
            >
              {item.organization}
            </Typography>
            <Chip
              variant="outlined"
              color="primary"
              size="sm"
              sx={(theme) => ({
                borderColor: theme.palette.divider,
                marginLeft: 'auto',
                flexShrink: 0,
                fontSize: '0.65rem',
                fontFamily: "'Fira Code', monospace",
              })}
            >
              {`${item.role}`}
            </Chip>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

export function CommunityOps() {
  return (
    <Stack gap={2} p={1} sx={{ overflow: 'hidden' }}>
      {details.communityops.map((item) => {
        const startDate = moment(item.start, 'MMM YYYY').toDate();
        const endDate = item.end === 'Present'
          ? new Date(Date.now())
          : moment(item.end, 'MMM YYYY').toDate();
        const momentDuration = moment.duration(endDate.getTime() - startDate.getTime());
        const years = momentDuration.years();
        const months = momentDuration.months() + 1;
        const duration = `${years > 0 ? `${years} year${years !== 1 ? 's' : ''} ` : ''}${months} month${months !== 1 ? 's' : ''}`;

        return (
          <Stack
            direction="row"
            gap={1.5}
            key={`${item.organization}-${item.position}-${item.start}-${item.end}`}
          >
            <Avatar
              color="neutral"
              variant="soft"
              size="lg"
              sx={(theme) => ({
                borderRadius: theme.getCssVar('radius-md'),
                border: `1px solid ${theme.getCssVar('palette-divider')}`,
              })}
            >
              {item.icon ? (
                <ProgressiveImage
                  src={item.icon}
                  placeholder={item.iconMin}
                  alt={item.organization}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              ) : (<FaHandsHelping />)}
            </Avatar>
            <Stack gap={0.5}>
              <Typography level="body1" display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "'Fira Code', monospace",
                    color: 'primary.400',
                    fontSize: '0.85em',
                  }}
                >
                  {'>_'}
                </Typography>
                {item.url ? (
                  <Typography
                    component="a"
                    href={item.url}
                    textColor="inherit"
                    target="_blank"
                    sx={{
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {item.organization}
                  </Typography>
                ) : item.organization}
                <Typography
                  level="body2"
                  component="span"
                  sx={{
                    fontFamily: "'Fira Code', monospace",
                    color: 'text.tertiary',
                    fontSize: '0.8em',
                  }}
                >
                  [
                  {formatTimestamp(item.start)}
                  {' → '}
                  {formatTimestamp(item.end)}
                  ]
                </Typography>
                <Chip
                  size="sm"
                  variant="soft"
                  color="primary"
                  sx={{
                    fontFamily: "'Fira Code', monospace",
                    fontSize: '0.7rem',
                  }}
                >
                  {duration}
                </Chip>
              </Typography>
              <Typography level="body2" display="flex" alignItems="baseline" flexWrap="wrap" gap={1}>
                <Typography fontWeight="lg" textColor="text.primary">
                  {item.position}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Fira Code', monospace",
                    color: 'text.secondary',
                    fontSize: '0.8em',
                  }}
                >
                  {item.location}
                </Typography>
              </Typography>
              <Stack direction="row" alignItems="start" flexWrap="wrap" gap={1} paddingY={0.5}>
                {item.skills && item.skills.map((skill) => (
                  <Chip
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    key={skill}
                    sx={(theme) => ({
                      borderColor: theme.palette.divider,
                      fontFamily: "'Fira Code', monospace",
                      fontSize: '0.7rem',
                    })}
                  >
                    {skill}
                  </Chip>
                ))}
              </Stack>
              <Typography level="body3" textColor="text.tertiary">
                {item.description}
              </Typography>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}

export default function Details({
  category,
}: {
  category: Category
}) {
  const transRef = useSpringRef();
  const container = React.createRef<HTMLDivElement>();

  const transition = useTransition(category, {
    ref: transRef,
    keys: null,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0, filter: 'blur(10px)', position: 'absolute' },
  });

  useEffect(() => {
    container.current?.parentElement?.scrollTo({ top: 0, behavior: 'smooth' });
    transRef.start();
  }, [category]);

  return (
    <Stack direction="column" ref={container}>
      {transition((style, item) => {
        switch (item) {
          case 'education':
            return (
              <animated.div style={style}>
                <Stack gap={3}>
                  <Education />
                  <Coursework />
                </Stack>
              </animated.div>
            );
          case 'experience':
            return (
              <animated.div style={style}>
                <Experience />
              </animated.div>
            );
          default:
            return (
              <animated.div style={style}>
                <Stack>
                  the currently selected category is
                  {' '}
                  {category}
                  {' '}
                  but there is no content for it yet.
                </Stack>
              </animated.div>
            );
        }
      })}
    </Stack>
  );
}
